import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { useHtClient } from '../providers/ht';

// Advisor-portal API client (backend /advisors/*). All endpoints require an
// authenticated advisor (require_advisor) and are firm-scoped server-side.

export interface Advisor {
  id: number;
  profile_id: string;
  firm_id: number;
  office_id?: number | null;
  role: 'advisor' | 'firm_admin';
  public_id: string;
  active: boolean;
  created_at: string;
}

export interface RoutingRule {
  id: number;
  firm_id: number;
  advisor_id: number;
  min_assets: number | null;
  max_assets: number | null;
  priority: number;
  active: boolean;
  created_at: string;
}

export interface AdvisorLead {
  id: number;
  first_name: string;
  last_name: string;
  email: string;
  phone?: string | null;
  quiz?: number | null;
  advisor_id?: number | null;
  firm_id?: number | null;
  source?: string | null;
  net_worth?: number | null;
  annual_income?: number | null;
  handled: boolean;
  handled_at?: string | null;
  created_at: string;
}

// "Who am I" — the calling advisor's own record. Returns 403 for non-advisors;
// the hook surfaces that as isError so the UI can hide advisor-only features.
export const useAdvisorMe = (options?: { enabled?: boolean }) => {
  const { data: client } = useHtClient();
  return useQuery({
    queryKey: ['advisor-me'],
    queryFn: async (): Promise<Advisor> => {
      if (!client) throw new Error('API client not initialized');
      const res = await client.get('/advisors/me');
      return res.data;
    },
    enabled: !!client && options?.enabled !== false,
    retry: false, // a 403 is a real answer (not an advisor), don't retry
    staleTime: 5 * 60 * 1000,
  });
};

// The advisor's book (firm-scoped). mineOnly / unhandledOnly drive the filters.
export const useAdvisorLeads = (
  opts?: { mineOnly?: boolean; unhandledOnly?: boolean; enabled?: boolean }
) => {
  const { data: client } = useHtClient();
  return useQuery({
    queryKey: ['advisor-leads', opts?.mineOnly, opts?.unhandledOnly],
    queryFn: async (): Promise<AdvisorLead[]> => {
      if (!client) throw new Error('API client not initialized');
      const params = new URLSearchParams();
      if (opts?.mineOnly) params.set('mine_only', 'true');
      if (opts?.unhandledOnly) params.set('unhandled_only', 'true');
      const qs = params.toString();
      const res = await client.get(`/advisors/leads${qs ? `?${qs}` : ''}`);
      return res.data;
    },
    enabled: !!client && opts?.enabled !== false,
    staleTime: 60 * 1000,
  });
};

export const useMarkLeadHandled = () => {
  const { data: client } = useHtClient();
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (leadId: number) => {
      if (!client) throw new Error('API client not initialized');
      const res = await client.put(`/advisors/leads/${leadId}/handled`);
      return res.data;
    },
    onSuccess: () => qc.invalidateQueries({ queryKey: ['advisor-leads'] }),
  });
};

// Routing rules (firm-admin configures the per-firm asset tiers).
export const useRoutingRules = (options?: { enabled?: boolean }) => {
  const { data: client } = useHtClient();
  return useQuery({
    queryKey: ['routing-rules'],
    queryFn: async (): Promise<RoutingRule[]> => {
      if (!client) throw new Error('API client not initialized');
      const res = await client.get('/advisors/routing-rules');
      return res.data;
    },
    enabled: !!client && options?.enabled !== false,
    retry: false,
    staleTime: 60 * 1000,
  });
};

export const useAdvisors = (options?: { enabled?: boolean }) => {
  const { data: client } = useHtClient();
  return useQuery({
    queryKey: ['firm-advisors'],
    queryFn: async (): Promise<Advisor[]> => {
      if (!client) throw new Error('API client not initialized');
      const res = await client.get('/advisors');
      return res.data;
    },
    enabled: !!client && options?.enabled !== false,
    retry: false,
    staleTime: 60 * 1000,
  });
};

export interface RoutingRuleInput {
  advisor_id: number;
  min_assets?: number | null;
  max_assets?: number | null;
  priority?: number;
}

export const useCreateRoutingRule = () => {
  const { data: client } = useHtClient();
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (rule: RoutingRuleInput) => {
      if (!client) throw new Error('API client not initialized');
      const res = await client.post('/advisors/routing-rules', rule);
      return res.data;
    },
    onSuccess: () => qc.invalidateQueries({ queryKey: ['routing-rules'] }),
  });
};

export const useDeleteRoutingRule = () => {
  const { data: client } = useHtClient();
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (ruleId: number) => {
      if (!client) throw new Error('API client not initialized');
      const res = await client.delete(`/advisors/routing-rules/${ruleId}`);
      return res.data;
    },
    onSuccess: () => qc.invalidateQueries({ queryKey: ['routing-rules'] }),
  });
};

// --- Firm branding (Marketing Kit, task 86bagxuwz) ---------------------------
// Any advisor reads their own firm's branding; only a firm_admin can change it.
export interface FirmBranding {
  id: number;
  name: string;
  logo_url: string | null;
  brand_primary: string | null;
  brand_secondary: string | null;
}

export const useFirmBranding = (options?: { enabled?: boolean }) => {
  const { data: client } = useHtClient();
  return useQuery({
    queryKey: ['advisor-branding'],
    queryFn: async (): Promise<FirmBranding> => {
      if (!client) throw new Error('API client not initialized');
      const res = await client.get('/advisors/branding');
      return res.data;
    },
    enabled: !!client && options?.enabled !== false,
    retry: false,
  });
};

export const useUpdateBranding = () => {
  const { data: client } = useHtClient();
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (body: { brand_primary?: string; brand_secondary?: string }): Promise<FirmBranding> => {
      if (!client) throw new Error('API client not initialized');
      const res = await client.put('/advisors/branding', body);
      return res.data;
    },
    onSuccess: (data) => qc.setQueryData(['advisor-branding'], data),
  });
};

export const useUploadLogo = () => {
  const { data: client } = useHtClient();
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (file: File): Promise<FirmBranding> => {
      if (!client) throw new Error('API client not initialized');
      const form = new FormData();
      form.append('file', file);
      // The shared client defaults to JSON, which would serialize the FormData to text.
      // Saying multipart here keeps it a file; the browser then fills in the boundary.
      const res = await client.post('/advisors/branding/logo', form, {
        headers: { 'Content-Type': 'multipart/form-data' },
      });
      return res.data;
    },
    onSuccess: (data) => qc.setQueryData(['advisor-branding'], data),
  });
};
